import { useState } from 'react';
import { LocateFixed, X } from 'lucide-react';
import { Field } from '@/components/Field';
import { Button } from '@/components/ui/button';
import { detectCity } from '@/lib/place';
import { cn } from '@/lib/utils';

/**
 * THE CITY FIELD, AND IT IS NOT A TEXT BOX (docs/5 § D260809-1521).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT WAS ONE, AND WHAT ARRIVED IN IT WAS WHATEVER SOMEBODY TYPED. A city on a card
 * is one of three or four things another member reads before deciding whether to
 * write, and the typed version of it is unverifiable by construction — so this
 * asks the device instead, and offers no way to type. Two actions, both one tap:
 * find it, or remove it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE WHOLE CONTROL IS THE BUTTON, and the × is a sibling rather than a child. A
 * 40px field with two icon buttons crowded at its end is the version of this that
 * had to be redrawn; a field that re-detects wherever it is tapped leaves the end of
 * it for the one destructive action, which is also the one that needs a target of
 * its own. Nested buttons would be neither valid nor reachable by a keyboard.
 *
 * IT LOOKS LIKE THE FIELDS AROUND IT because it is `Button variant="outline"` at the
 * app's control height, which resolves to the same border and the same dark-mode
 * wash as `Input` — the same tokens rather than a copy of its class list, so the two
 * cannot drift apart when the theme moves.
 *
 * NO SPINNER AND NO SENTENCE WHILE IT WORKS. The label says *Locating*, which is the
 * same treatment every other action in this product gives a request in flight
 * (*Saving*, *Sending*) — see `states.tsx` for why there is no spinner anywhere.
 *
 * WHAT IS ALREADY ON A CARD IS SHOWN AS IT IS. Cities typed before this control
 * existed are neither re-checked nor cleared: the value renders, and it changes only
 * if its owner asks for that. A form that silently corrected somebody's own writing
 * about themselves would be a worse trade than an old string.
 */
export function PlaceField({
  value,
  onChange,
  error,
}: {
  /** The city as it will be saved. The empty string is "there is none". */
  value: string;
  onChange: (city: string) => void;
  /** A refusal from anywhere else — the server, or the form's own rules. */
  error?: string | null;
}) {
  const [busy, setBusy] = useState(false);
  /** The last refusal from the device or the lookup. Cleared by trying again. */
  const [failure, setFailure] = useState<string | null>(null);

  const locate = async () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      onChange(await detectCity());
    } catch (thrown) {
      setFailure(thrown instanceof Error ? thrown.message : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label="City" optional error={error ?? failure}>
      {(props) => (
        <div className="relative">
          <Button
            {...props}
            type="button"
            variant="outline"
            onClick={() => void locate()}
            disabled={busy}
            /* `pe-10` keeps a long city name from running under the ×, and only
               where there is one. `min-w-0` is what lets the span below actually
               truncate: a flex child will not shrink under its content without it,
               so the ellipsis is a no-op and the name pushes the field open. */
            className={cn(
              'h-10 w-full justify-start gap-2.5 px-3 text-[15px] font-normal',
              value && 'pe-10',
            )}
          >
            <LocateFixed aria-hidden className="text-muted-foreground" />
            {/* THE ACTION IS MUTED AND A CITY IS NOT, which is the same contrast
                every other field on this form uses for the difference between a
                placeholder and an answer. At full weight *Use my location* reads as
                a value somebody has already chosen, and a form whose empty fields
                look filled in is a form nobody finishes. */}
            <span
              className={cn(
                'min-w-0 truncate',
                /* Not while it works: `disabled:opacity-50` is already on the
                   button, and muting a faded label twice makes it unreadable. */
                !value && !busy && 'text-muted-foreground',
              )}
            >
              {busy ? 'Locating' : value || 'Use my location'}
            </span>
          </Button>
          {value && !busy && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Remove city"
              onClick={() => {
                setFailure(null);
                onChange('');
              }}
              className="absolute end-1.5 top-1/2 -translate-y-1/2"
            >
              <X aria-hidden className="text-muted-foreground" />
            </Button>
          )}
        </div>
      )}
    </Field>
  );
}
